// @spec GHG-STR-007
// TanStack Query hooks for emission factor API

import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  EmissionFactorDetail,
  EmissionFactorSearchParams,
  EmissionFactorSearchResponse,
  EstimateRequest,
  EstimateResponse,
  EstimateValidationResult,
  FactorParameterSpec,
  PinFactorRequest,
  PinFactorResponse,
  PinnedFactorsResponse,
  SyncResponse,
} from '../types';

import { useGHGOrganizationId } from './use-ghg-organization-id';

import { createApiClient } from '@/lib/api-client';
import { ApiClientError } from '@/types/api';

/**
 * Query key factory for emission factors.
 */
export const emissionFactorKeys = {
  all: ['emission-factors'] as const,
  search: (orgId: string, params: EmissionFactorSearchParams) =>
    [...emissionFactorKeys.all, 'search', orgId, params] as const,
  detail: (orgId: string, factorId: string) =>
    [...emissionFactorKeys.all, 'detail', orgId, factorId] as const,
  pinned: (orgId: string) => [...emissionFactorKeys.all, 'pinned', orgId] as const,
};

/**
 * Build query string from search parameters.
 */
function buildQueryString(params: EmissionFactorSearchParams): string {
  const searchParams = new URLSearchParams();

  if (params.query) {
    searchParams.set('query', params.query);
  }
  if (params.activityId) {
    searchParams.set('activity_id', params.activityId);
  }
  if (params.scope !== undefined) {
    searchParams.set('scope', String(params.scope));
  }
  if (params.category) {
    searchParams.set('category', params.category);
  }
  if (params.sector) {
    searchParams.set('sector', params.sector);
  }
  if (params.source) {
    searchParams.set('source', params.source);
  }
  if (params.sourceDataset) {
    searchParams.set('source_dataset', params.sourceDataset);
  }
  if (params.sourceLcaActivity) {
    searchParams.set('source_lca_activity', params.sourceLcaActivity);
  }
  if (params.accessType) {
    searchParams.set('access_type', params.accessType);
  }
  if (params.allowedDataQualityFlags?.length) {
    params.allowedDataQualityFlags.forEach((flag) => {
      searchParams.append('allowed_data_quality_flags', flag);
    });
  }
  if (params.region) {
    searchParams.set('region', params.region);
  }
  if (params.year !== undefined) {
    searchParams.set('year', String(params.year));
  }
  if (params.unitType) {
    searchParams.set('unit_type', params.unitType);
  }
  if (params.calculationMethod) {
    searchParams.set('calculation_method', params.calculationMethod);
  }
  if (params.factorSource && params.factorSource !== 'all') {
    searchParams.set('factor_source', params.factorSource);
  }
  if (params.usedOnly !== undefined) {
    searchParams.set('used_only', String(params.usedOnly));
  }
  if (params.page !== undefined) {
    searchParams.set('page', String(params.page));
  }
  if (params.pageSize !== undefined) {
    searchParams.set('page_size', String(params.pageSize));
  }

  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : '';
}

/**
 * Hook to search emission factors.
 */
export function useEmissionFactorSearch(
  params: EmissionFactorSearchParams,
  options?: { enabled?: boolean }
) {
  const { getToken } = useAuth();
  const orgId = useGHGOrganizationId() ?? '';

  return useQuery({
    queryKey: emissionFactorKeys.search(orgId, params),
    queryFn: async () => {
      const apiClient = createApiClient({
        getAuthToken: getToken,
      });

      const queryString = buildQueryString(params);
      try {
        return await apiClient.get<EmissionFactorSearchResponse>(
          `/organizations/${orgId}/ghg/emission-factors/search${queryString}`
        );
      } catch (error) {
        if (error instanceof ApiClientError && error.status === 422 && params.scope !== undefined) {
          const fallbackParams: EmissionFactorSearchParams = { ...params };
          delete fallbackParams.scope;
          const fallbackQueryString = buildQueryString(fallbackParams);
          return await apiClient.get<EmissionFactorSearchResponse>(
            `/organizations/${orgId}/ghg/emission-factors/search${fallbackQueryString}`
          );
        }
        throw error;
      }
    },
    enabled: Boolean(orgId) && (options?.enabled ?? true),
  });
}

/**
 * Hook to get emission factor details.
 */
export function useEmissionFactor(factorId: string, options?: { enabled?: boolean }) {
  const { getToken } = useAuth();
  const orgId = useGHGOrganizationId() ?? '';

  return useQuery({
    queryKey: emissionFactorKeys.detail(orgId, factorId),
    queryFn: async () => {
      const apiClient = createApiClient({
        getAuthToken: getToken,
      });

      return apiClient.get<EmissionFactorDetail>(
        `/organizations/${orgId}/ghg/emission-factors/${factorId}`
      );
    },
    enabled: Boolean(orgId) && Boolean(factorId) && (options?.enabled ?? true),
  });
}

/**
 * Hook to list pinned factors.
 */
export function usePinnedFactors() {
  const { getToken } = useAuth();
  const orgId = useGHGOrganizationId() ?? '';

  return useQuery({
    queryKey: emissionFactorKeys.pinned(orgId),
    queryFn: async () => {
      const apiClient = createApiClient({
        getAuthToken: getToken,
      });

      return apiClient.get<PinnedFactorsResponse>(
        `/organizations/${orgId}/ghg/emission-factors/pinned`
      );
    },
    enabled: Boolean(orgId),
  });
}

/**
 * Hook to pin an emission factor.
 */
export function usePinFactor() {
  const { getToken } = useAuth();
  const orgId = useGHGOrganizationId() ?? '';
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ factorId, data }: { factorId: string; data?: PinFactorRequest }) => {
      const apiClient = createApiClient({
        getAuthToken: getToken,
      });

      return apiClient.post<PinFactorResponse>(
        `/organizations/${orgId}/ghg/emission-factors/${factorId}/pin`,
        data
      );
    },
    onSuccess: (_, variables) => {
      // Invalidate pinned factors list
      void queryClient.invalidateQueries({
        queryKey: emissionFactorKeys.pinned(orgId),
      });
      // Invalidate the specific factor to update isPinned status
      void queryClient.invalidateQueries({
        queryKey: emissionFactorKeys.detail(orgId, variables.factorId),
      });
    },
  });
}

/**
 * Hook to unpin an emission factor.
 */
export function useUnpinFactor() {
  const { getToken } = useAuth();
  const orgId = useGHGOrganizationId() ?? '';
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (factorId: string) => {
      const apiClient = createApiClient({
        getAuthToken: getToken,
      });

      await apiClient.delete(`/organizations/${orgId}/ghg/emission-factors/${factorId}/pin`);
    },
    onSuccess: (_, factorId) => {
      // Invalidate pinned factors list
      void queryClient.invalidateQueries({
        queryKey: emissionFactorKeys.pinned(orgId),
      });
      // Invalidate the specific factor to update isPinned status
      void queryClient.invalidateQueries({
        queryKey: emissionFactorKeys.detail(orgId, factorId),
      });
    },
  });
}

/**
 * Hook to trigger emission factor sync (admin only).
 */
export function useTriggerSync() {
  const { getToken } = useAuth();
  const orgId = useGHGOrganizationId() ?? '';

  return useMutation({
    mutationFn: async () => {
      const apiClient = createApiClient({
        getAuthToken: getToken,
      });

      return apiClient.post<SyncResponse>(`/organizations/${orgId}/ghg/emission-factors/sync`);
    },
  });
}

// =========================================================================
// Estimate hooks (GHG-STR-071, GHG-STR-078, GHG-STR-083)
// =========================================================================

/**
 * Build a normalized emission factor selector that enforces XOR:
 * either `id` OR `activityId` (+ optional `dataVersion`) — never both.
 *
 * In factor-detail context (id present), only `id` is sent.
 * In manual / activity context, only `activityId` is sent.
 */
export function buildEstimateSelector(input: {
  id?: string;
  activityId?: string;
  dataVersion?: string;
}): EstimateRequest['emissionFactor'] {
  if (input.id) {
    return { id: input.id };
  }
  const selector: EstimateRequest['emissionFactor'] = {};
  if (input.activityId) {
    selector.activityId = input.activityId;
    if (input.dataVersion) {
      selector.dataVersion = input.dataVersion;
    }
  }
  return selector;
}

/**
 * Hook to get the parameter spec for a factor.
 */
export function useFactorParameterSpec(factorId: string, options?: { enabled?: boolean }) {
  const { getToken } = useAuth();
  const orgId = useGHGOrganizationId() ?? '';

  return useQuery({
    queryKey: [...emissionFactorKeys.all, 'parameter-spec', orgId, factorId] as const,
    queryFn: async () => {
      const apiClient = createApiClient({
        getAuthToken: getToken,
      });

      return apiClient.get<FactorParameterSpec>(
        `/organizations/${orgId}/ghg/emission-factors/${factorId}/parameter-spec`
      );
    },
    enabled: Boolean(orgId) && Boolean(factorId) && (options?.enabled ?? true),
  });
}

/**
 * Hook to validate estimate parameters.
 */
export function useValidateEstimate() {
  const { getToken } = useAuth();
  const orgId = useGHGOrganizationId() ?? '';

  return useMutation({
    mutationFn: async (data: EstimateRequest) => {
      const apiClient = createApiClient({
        getAuthToken: getToken,
      });

      return apiClient.post<EstimateValidationResult>(
        `/organizations/${orgId}/ghg/emission-factors/estimate/validate`,
        data
      );
    },
  });
}

/**
 * Hook to execute a Climatiq estimate.
 */
export function useRunEstimate() {
  const { getToken } = useAuth();
  const orgId = useGHGOrganizationId() ?? '';

  return useMutation({
    mutationFn: async (data: EstimateRequest) => {
      const apiClient = createApiClient({
        getAuthToken: getToken,
      });

      return apiClient.post<EstimateResponse>(
        `/organizations/${orgId}/ghg/emission-factors/estimate`,
        data
      );
    },
  });
}

/**
 * Validate-then-execute orchestration result.
 */
export interface ValidateAndEstimateResult {
  validation: EstimateValidationResult;
  estimate?: EstimateResponse;
}

/**
 * Hook that orchestrates the two-stage validate-then-execute estimate workflow.
 * Validates parameters first; if valid, executes the estimate.
 * Returns field-level errors from validation or the final estimate result.
 */
export function useValidateAndEstimate() {
  const { getToken } = useAuth();
  const orgId = useGHGOrganizationId() ?? '';

  return useMutation({
    mutationFn: async (data: EstimateRequest): Promise<ValidateAndEstimateResult> => {
      const apiClient = createApiClient({
        getAuthToken: getToken,
      });

      const basePath = `/organizations/${orgId}/ghg/emission-factors`;

      // Stage 1: validate
      const validation = await apiClient.post<EstimateValidationResult>(
        `${basePath}/estimate/validate`,
        data
      );

      if (!validation.valid) {
        return { validation };
      }

      // Stage 2: execute
      const estimate = await apiClient.post<EstimateResponse>(
        `${basePath}/estimate`,
        data
      );

      return { validation, estimate };
    },
  });
}
